import { MountingID, MountingSystemType } from 'types/mounting'
import { getVanDerValkRoofTypes } from './mounting_systems/VanDerValk'
import getNativeMountingSystems from './mountingSystems'
import { MountingCalcInput, RoofTypeName } from './types'

const minimalMountingCalcInput: MountingCalcInput = {
  panelBlocks: [],
  roof: { roofIndex: 0, roofTypeName: null },
  options: {},
}

/**
 * Returns whether roof type must be selected before mounting system options are shown.
 * Ground mounts and systems that disable roof type selection do not require a roof type first.
 */
export function isRoofTypeRequiredForMountingSelection(
  selectedMountingType: string | undefined,
  isRoofTypeSelectorDisabled: boolean
): boolean {
  if (isRoofTypeSelectorDisabled) {
    return false
  }
  if (selectedMountingType === 'ground') {
    return false
  }
  return true
}

function isMountingCompatibleWithRoofTypeForSelector(
  mountingId: MountingID,
  roofTypeName: RoofTypeName,
  mountingSystemType?: MountingSystemType,
  nativeSystems?: ReturnType<typeof getNativeMountingSystems>
): boolean {
  if (mountingId === 'vanDerValk' && mountingSystemType) {
    return getVanDerValkRoofTypes(mountingSystemType).includes(roofTypeName)
  }
  return isMountingCompatibleWithRoofType(mountingId, roofTypeName, nativeSystems)
}

/**
 * Returns mounting IDs compatible with the given roof type.
 * When roof type is not selected, returns the input list unchanged.
 */
export function filterMountingIdsByRoofType(
  mountingIds: MountingID[],
  roofTypeName: RoofTypeName | null,
  nativeSystems?: ReturnType<typeof getNativeMountingSystems>,
  mountingSystemType?: MountingSystemType
): MountingID[] {
  if (roofTypeName === null) {
    return mountingIds
  }

  return mountingIds.filter((mountingId) =>
    isMountingCompatibleWithRoofTypeForSelector(mountingId, roofTypeName, mountingSystemType, nativeSystems)
  )
}

/**
 * Returns mounting IDs that are incompatible with the given roof type.
 * When roof type is not selected, returns an empty list.
 */
export function filterIncompatibleMountingIdsByRoofType(
  mountingIds: MountingID[],
  roofTypeName: RoofTypeName | null,
  nativeSystems?: ReturnType<typeof getNativeMountingSystems>,
  mountingSystemType?: MountingSystemType
): MountingID[] {
  if (roofTypeName === null) {
    return []
  }

  return mountingIds.filter(
    (mountingId) =>
      !isMountingCompatibleWithRoofTypeForSelector(mountingId, roofTypeName, mountingSystemType, nativeSystems)
  )
}

/**
 * Returns whether the given mounting is compatible with the given roof type for UI display.
 * - If roof type is not selected (null), returns true for all.
 * - For native mountings with roofTypes in getCompatibilityParameters(), returns true only if roof type is in the list.
 * - For partner mountings or native without roofTypes, returns true (no restriction).
 */
export function isMountingCompatibleWithRoofType(
  mountingId: MountingID,
  roofTypeName: RoofTypeName | null,
  nativeSystems?: ReturnType<typeof getNativeMountingSystems>
): boolean {
  if (roofTypeName === null) {
    return true
  }

  const systems = nativeSystems ?? getNativeMountingSystems()
  const mountingDef = systems[mountingId as keyof typeof systems]
  if (!mountingDef?.class) {
    return true
  }

  try {
    const MountingClass = mountingDef.class
    const instance = new MountingClass(minimalMountingCalcInput)
    const params = instance.getCompatibilityParameters?.()
    const roofTypes = params?.roofTypes
    if (!roofTypes || roofTypes.length === 0) {
      return true
    }
    return roofTypes.includes(roofTypeName)
  } catch {
    return true
  }
}

/**
 * Returns the roof types a native mounting system declares as compatible, if any.
 */
export function getMountingCompatibleRoofTypes(
  mountingId: MountingID,
  nativeSystems?: ReturnType<typeof getNativeMountingSystems>
): RoofTypeName[] | null {
  const systems = nativeSystems ?? getNativeMountingSystems()
  const mountingDef = systems[mountingId as keyof typeof systems]
  if (!mountingDef?.class) {
    return null
  }

  try {
    const MountingClass = mountingDef.class
    const instance = new MountingClass(minimalMountingCalcInput)
    const roofTypes = instance.getCompatibilityParameters?.()?.roofTypes
    return roofTypes?.length ? roofTypes : null
  } catch {
    return null
  }
}
